import type { ClosePermissionStatus } from '../shared/close-permission';
import { registerPageHold } from './page-holds';

/**
 * A correction typed and not saved, or a save still on its way, when the tab is closed or
 * reloaded. CapturePoint refuses to close its window over either and says why
 * (renderer/close-permission.ts); a browser tab has no such refusal, only its own "Leave site?"
 * question, so the same blockers ask it: only while one of them says `drafts` or `saving`, which
 * is something to lose. A blocker that is `unavailable`, or cannot answer, has nothing it can name,
 * so the browser is not made to ask over it. Anything but `idle` still holds the page against
 * Paige reloading itself (page-holds.ts), as before.
 */
const blockers = new Set<() => ClosePermissionStatus>();
let listening = false;

/** A blocker of CapturePoint's close, in the browser; the returned release takes it away again. */
export function holdForReviewEdits(read: () => ClosePermissionStatus): () => void {
  listen();
  blockers.add(read);
  const release = registerPageHold('review-edits', () => read() !== 'idle');
  return () => {
    blockers.delete(read);
    release();
  };
}

/** Whether leaving now would lose a correction: a draft in a field, or a save that has not landed. */
export function reviewEditsUnsaved(): boolean {
  for (const read of blockers) {
    try {
      const status = read();
      if (status === 'drafts' || status === 'saving') return true;
    } catch {
      // A blocker that cannot answer names nothing to lose; the reload hold still holds for it.
    }
  }
  return false;
}

function listen(): void {
  if (listening || typeof window === 'undefined' || !window.addEventListener) return;
  listening = true;
  window.addEventListener('beforeunload', (event) => {
    if (!reviewEditsUnsaved()) return;
    event.preventDefault();
    event.returnValue = '';
  });
}
